/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { JSONProp } from 'componentregistry';
import CodeInput from 'custom/CodeInput';
import { helpTextSelector } from 'designer/state';
import useComponentSize from 'hooks/useComponentSize';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useState } from 'react';
import { useTheme } from 'theme/Theme';
import { getErrorMessage, isEmpty } from 'utils/isEmpty';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    cursor: 'default',
  },
});

export function JSONDialog({
  value,
  onSubmit,
  onClose,
}: {
  value: string;
  onSubmit: (val: string) => void;
  onClose: () => void;
}) {
  const [jsonData, setJSONData] = useState(value);
  const platform = usePlatform();
  const theme = useTheme();
  const s = useComponentSize();

  return (
    <Dialog fullScreen open onClose={onClose}>
      <DialogTitle>
        JSON Data
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 16 }}
          aria-label="close"
          size="medium"
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon width={24} height={24} iconName="times" />
        </IconButton>
      </DialogTitle>
      <Typography sx={{ marginLeft: '24px' }} variant="caption">
        Copy &amp; Paste JSON data (Max 20K Objects). The first object will
        determine the data type of the fields and hence must not have any empty
        values.
        <ul>
          <li>
            Date values must be in ISO format e.g. 2021-12-31 or
            2021-12-31T23:59:59.123Z.
          </li>
          <li>
            A value &quot;true&quot; or &quot;false&quot; will be treated as
            boolean.
          </li>
          <li>
            &quot;Y&quot; or &quot;N&quot; will be treated as YN type that are
            used to render Checkboxes.
          </li>
          <li>
            &quot;T&quot; or &quot;F&quot; will be treated as TF type that are
            used to render Checkboxes.
          </li>
        </ul>
      </Typography>
      <DialogContent
        ref={s.ref}
        data-testid="json-data-content"
        sx={{ overflow: 'hidden', p: 0 }}
      >
        <CodeInput
          darkMode={theme.palette.mode !== 'light'}
          height={s.height}
          width={s.width}
          value={jsonData ?? ''}
          onChange={setJSONData}
          language="json"
        />
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Icon parentColor="primary" iconName="check" />}
          onClick={async () => {
            if (isEmpty(jsonData)) {
              onSubmit(jsonData);
              return;
            }
            let json: Record<string, unknown>[] = [];
            try {
              json = JSON.parse(jsonData.trim());
            } catch (e) {
              platform.showError(getErrorMessage(e));
              return;
            }
            if (!Array.isArray(json)) {
              platform.showError('You must provide an array of objects!');
            }
            if (json.length === 0) {
              platform.showError(
                'You must provide atleast one object of data!',
              );
              return;
            }
            if (json.length > 20000) {
              platform.showError(
                'You can load only a max of 20K rows at a time.',
              );
              return;
            }
            const data = json[0];
            const emptyKey = Object.keys(data).find((key) =>
              isEmpty(data[key]),
            );
            if (emptyKey) {
              platform.showError(
                `Found an empty value in row 1, key ${emptyKey}. The first object must not have empty values! It is used to determine the data type of all the fields.`,
              );
              return;
            }
            onSubmit(jsonData);
          }}
          variant="contained"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function JSONProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
  sampleData,
}: PropertyEditorProps<JSONProp, string>) {
  const classes = useStyles();
  const [edit, setEdit] = useState(false);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  const val =
    value && value.length > 2000
      ? `JSON too large! - ${value.length} characters
Click the edit icon to view/edit.`
      : value ?? '';

  return (
    <>
      <TextField
        disabled={isViewOnlyUser}
        data-testid={property}
        className={classes.root}
        fullWidth
        label={def.label}
        variant="standard"
        required={required}
        spellCheck={false}
        value={val}
        multiline
        minRows={5}
        maxRows={15}
        onFocus={(e) => {
          e.target.select();
        }}
        classes={{ root: classes.input }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setEdit(true);
              }}
              position="start"
            >
              <Icon iconName="edit" data-tip="Edit" />
            </InputAdornment>
          ),
          endAdornment: helpText ? (
            <InputAdornment position="end">
              <Icon
                className={classes.tip}
                iconName="comment-exclamation"
                data-tip={helpText}
                data-tip-delay={0}
                data-tip-at="top"
              />
            </InputAdornment>
          ) : undefined,
        }}
      />
      {edit ? (
        <JSONDialog
          value={
            value ||
            sampleData ||
            `[
    {
        "firstName": "John",
        "number": 123.456,
        "dateTime": "2022-02-02T22:10:09.276Z",
        "boolean": true,
        "checkbox": "Y"
    }
]`
          }
          onSubmit={(_val) => {
            onChange(property, _val, true);
            setEdit(false);
          }}
          onClose={() => {
            setEdit(false);
          }}
        />
      ) : null}
    </>
  );
}

export default memo(JSONProperty);
